/**
 * API service for invoicing functionality
 * Handles authenticated requests to the invoicing API
 */

import { auth } from '../firebase-config';

const INVOICING_API_URL = process.env.REACT_APP_INVOICING_API_URL || 'http://localhost:8085';

/**
 * Make an authenticated API request to the invoicing service
 * @param {string} endpoint - API endpoint (e.g., '/api/invoicing/v1/invoices')
 * @param {object} options - Fetch options
 * @returns {Promise<object>} Response data
 */
export async function makeInvoicingRequest(endpoint, options = {}) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${INVOICING_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  // Handle PDF download separately
  if (options.responseType === 'blob') {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.message || 'Request failed');
    }
    return response.blob();
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    // Same shape as api.js: callers read err.status / err.body to tell a 409
    // ("no billing entity") from a 500, and to show the server's message.
    const err = new Error(error.message || error.error || 'Request failed');
    err.status = response.status;
    err.body = error;
    throw err;
  }

  return response.json();
}

// ============================================================================
// Invoice Endpoints
// ============================================================================

export const invoiceApi = {
  /**
   * List invoices with filtering and pagination
   * @param {object} params - Query parameters
   */
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.customer_billing_entity_id) searchParams.append('customer_billing_entity_id', params.customer_billing_entity_id);
    if (params.document_status) searchParams.append('document_status', params.document_status);
    // issued | disputed | settled | cancelled, or 'overdue' (issued, past its due date). Payment lives on the settlement.
    if (params.settlement_status) searchParams.append('settlement_status', params.settlement_status);
    if (params.vat_treatment) searchParams.append('vat_treatment', params.vat_treatment);
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset !== undefined) searchParams.append('offset', params.offset);
    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.sort_order) searchParams.append('sort_order', params.sort_order);
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/invoices?${searchParams}`);
  },

  /**
   * Get invoice by ID
   * @param {string} invoiceId - Invoice UUID
   * @param {object} params - Optional params (test_mode)
   */
  get: (invoiceId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.test_mode) searchParams.append('test_mode', 'true');
    const queryString = searchParams.toString();
    return makeInvoicingRequest(`/api/invoicing/v1/invoices/${invoiceId}${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Create an invoice manually
   * @param {object} data - Invoice data
   */
  create: (data) =>
    makeInvoicingRequest('/api/invoicing/v1/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Create invoice from uninvoiced commissions
   * @param {object} data - Period and retailer info
   */
  createFromCommissions: (data) =>
    makeInvoicingRequest('/api/invoicing/v1/invoices/from-commissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Cancel an invoice
   * @param {string} invoiceId - Invoice UUID
   * @param {object} data - Cancellation details (reason, test_mode)
   */
  cancel: (invoiceId, data = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/invoices/${invoiceId}/cancel`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Generate PDF for invoice
   * @param {string} invoiceId - Invoice UUID
   */
  generatePdf: (invoiceId) =>
    makeInvoicingRequest(`/api/invoicing/v1/invoices/${invoiceId}/pdf`, {
      method: 'POST',
    }),

  /**
   * Get PDF download URL
   * @param {string} invoiceId - Invoice UUID
   */
  getPdfUrl: (invoiceId) =>
    makeInvoicingRequest(`/api/invoicing/v1/invoices/${invoiceId}/pdf`),

  /**
   * Get PDF preview URL (inline display)
   * @param {string} invoiceId - Invoice UUID
   * @returns {string} URL for PDF preview
   */
  getPdfPreviewUrl: (invoiceId) =>
    `${INVOICING_API_URL}/api/invoicing/v1/invoices/${invoiceId}/pdf/preview`,

  /**
   * Download PDF as blob
   * @param {string} invoiceId - Invoice UUID
   * @param {object} params - Optional params (test_mode)
   */
  downloadPdf: (invoiceId, params = {}) => {
    const searchParams = new URLSearchParams({ download: 'true' });
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/invoices/${invoiceId}/pdf?${searchParams}`, {
      responseType: 'blob',
    });
  },
};

// ============================================================================
// Billing Run Endpoints — one run issues invoice + self-bill + settlement per entity
// ============================================================================

const withTestMode = (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.test_mode) searchParams.append('test_mode', 'true');
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

export const billingRunApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset !== undefined) searchParams.append('offset', params.offset);
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/billing-runs?${searchParams}`);
  },

  get: (runId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/billing-runs/${runId}${withTestMode(params)}`),

  // data: supply_period_start, supply_period_end, target_billing_entity_ids?, test_mode?
  preview: (data) =>
    makeInvoicingRequest('/api/invoicing/v1/billing-runs/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // data as preview, plus hold_delivery (default true: prepare everything, send on release).
  create: (data) =>
    makeInvoicingRequest('/api/invoicing/v1/billing-runs', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Sends every unsent settlement email of a held run; stamps sent_at / due dates.
  release: (runId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/billing-runs/${runId}/release`, {
      method: 'POST',
      body: JSON.stringify({ test_mode: params.test_mode || false }),
    }),

  cancel: (runId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/billing-runs/${runId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ test_mode: params.test_mode || false }),
    }),
};

// ============================================================================
// Settlement Endpoints — the one net cash movement per entity per period
// ============================================================================

export const settlementApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.billing_entity_id) searchParams.append('billing_entity_id', params.billing_entity_id);
    if (params.status) searchParams.append('status', params.status);
    if (params.net_direction) searchParams.append('net_direction', params.net_direction);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset !== undefined) searchParams.append('offset', params.offset);
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/settlements?${searchParams}`);
  },

  get: (settlementId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/settlements/${settlementId}${withTestMode(params)}`),

  // Both required when the settlement moves cash; a balanced one settles without.
  recordCash: (settlementId, data = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/settlements/${settlementId}/record-cash-settlement`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resend: (settlementId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/settlements/${settlementId}/resend`, {
      method: 'POST',
      body: JSON.stringify({ test_mode: params.test_mode || false }),
    }),

  resolveDispute: (settlementId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/settlements/${settlementId}/resolve-dispute`, {
      method: 'POST',
      body: JSON.stringify({ test_mode: params.test_mode || false }),
    }),

  downloadStatement: (settlementId, params = {}) => {
    const searchParams = new URLSearchParams({ download: 'true' });
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/settlements/${settlementId}/statement?${searchParams}`, {
      responseType: 'blob',
    });
  },
};

// ============================================================================
// Self-Billed Invoice Endpoints — the retailer's invoice to HandOff, issued by HandOff
// ============================================================================

export const selfBillApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.supplier_billing_entity_id) searchParams.append('supplier_billing_entity_id', params.supplier_billing_entity_id);
    if (params.review_status) searchParams.append('review_status', params.review_status);
    if (params.document_status) searchParams.append('document_status', params.document_status);
    if (params.settlement_status) searchParams.append('settlement_status', params.settlement_status);
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset !== undefined) searchParams.append('offset', params.offset);
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/self-bills?${searchParams}`);
  },

  get: (selfBillId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/self-bills/${selfBillId}${withTestMode(params)}`),

  downloadPdf: (selfBillId, params = {}) => {
    const searchParams = new URLSearchParams({ download: 'true' });
    if (params.test_mode) searchParams.append('test_mode', 'true');
    return makeInvoicingRequest(`/api/invoicing/v1/self-bills/${selfBillId}/pdf?${searchParams}`, {
      responseType: 'blob',
    });
  },

  // Admin records the retailer's emailed rejection (Decision 8); disputes the settlement.
  reject: (selfBillId, data = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/self-bills/${selfBillId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  accept: (selfBillId, params = {}) =>
    makeInvoicingRequest(`/api/invoicing/v1/self-bills/${selfBillId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ test_mode: params.test_mode || false }),
    }),
};

// ============================================================================
// VAT Validation Endpoints
// ============================================================================

export const vatApi = {
  /**
   * Validate a VAT number via VIES
   * @param {string} vatNumber - VAT number to validate
   */
  validate: (vatNumber) =>
    makeInvoicingRequest('/api/invoicing/v1/vat/validate', {
      method: 'POST',
      body: JSON.stringify({ vat_number: vatNumber }),
    }),

  /**
   * Validate a billing entity's VAT number via VIES and store the verdict on the entity.
   * @param {string} entityId - Billing entity UUID
   * @param {string} vatNumber - VAT number
   * @param {boolean} forceRevalidation - Force VIES call
   */
  updateEntityVat: (entityId, vatNumber, forceRevalidation = false) =>
    makeInvoicingRequest(`/api/invoicing/v1/vat/entity/${entityId}`, {
      method: 'POST',
      body: JSON.stringify({
        vat_number: vatNumber,
        force_revalidation: forceRevalidation,
      }),
    }),

  /**
   * Validate the VAT number of the entity a storefront bills through.
   * 409 (err.status) with error 'no_billing_entity' when the storefront is unlinked.
   * @param {string} retailerId - Retailer ID
   * @param {string} vatNumber - VAT number
   * @param {boolean} forceRevalidation - Force VIES call
   */
  updateRetailerVat: (retailerId, vatNumber, forceRevalidation = false) =>
    makeInvoicingRequest(`/api/invoicing/v1/vat/retailer/${retailerId}`, {
      method: 'POST',
      body: JSON.stringify({
        vat_number: vatNumber,
        force_revalidation: forceRevalidation,
      }),
    }),
};

// ============================================================================
// Retailer Billing Endpoints
// ============================================================================

export const billingApi = {
  /**
   * Get retailer billing information
   * @param {string} retailerId - Retailer ID
   */
  getRetailerBilling: (retailerId) =>
    makeInvoicingRequest(`/api/invoicing/v1/retailers/${retailerId}/billing`),

  /**
   * Get uninvoiced commissions for a retailer
   * @param {string} retailerId - Retailer ID (buying_retailer_id)
   * @param {string} periodStart - Period start (YYYY-MM-DD)
   * @param {string} periodEnd - Period end (YYYY-MM-DD)
   */
  getUninvoicedCommissions: (retailerId, periodStart, periodEnd) => {
    const searchParams = new URLSearchParams({
      supply_period_start: periodStart,
      supply_period_end: periodEnd,
    });
    return makeInvoicingRequest(`/api/invoicing/v1/retailers/${retailerId}/commissions/uninvoiced?${searchParams}`);
  },
};

// ============================================================================
// Combined export for convenience
// ============================================================================

export const invoicingApi = {
  invoices: invoiceApi,
  runs: billingRunApi,
  settlements: settlementApi,
  selfBills: selfBillApi,
  vat: vatApi,
  billing: billingApi,
};

export default invoicingApi;
